import { motion, AnimatePresence } from 'framer-motion'
import { SlidersHorizontal, ChevronUp, X as XIcon } from 'lucide-react'
import type { SortBy, StatusFilter } from '../../hooks/useCatalogFilters'

interface Props {
  isOpen: boolean
  onToggle: () => void
  activeFilterCount: number
  prefersReducedMotion: boolean
  // filter state
  statusFilter: StatusFilter
  setStatusFilter: (v: StatusFilter) => void
  inStockOnly: boolean
  setInStockOnly: (v: boolean) => void
  priceMin: number | ''
  setPriceMin: (v: number | '') => void
  priceMax: number | ''
  setPriceMax: (v: number | '') => void
  sortBy: SortBy
  setSortBy: (v: SortBy) => void
  clearFilters: () => void
}

export function FilterToggleButton({ isOpen, onToggle, activeFilterCount, mobile = false }: {
  isOpen: boolean
  onToggle: () => void
  activeFilterCount: number
  mobile?: boolean
}) {
  return (
    <button
      onClick={onToggle}
      className={`${mobile
        ? 'hidden flex items-center gap-1.5 px-3 py-2 rounded-full border-none text-[12px] cursor-pointer transition-all duration-200 shrink-0'
        : 'flex items-center gap-3.5 px-4 py-2 rounded-full border-none text-[13px] cursor-pointer transition-all duration-200 mr-auto'
      } ${isOpen || activeFilterCount > 0
        ? 'bg-[#1A1A1A] text-white font-semibold'
        : 'bg-[#F2EDE4] text-[#6B6560] font-medium hover:bg-[#EAE4DB]'
      }`}
      aria-expanded={isOpen}
    >
      <SlidersHorizontal className="w-3.5 h-3.5" />
      סינון
      {activeFilterCount > 0 && (
        <span className="w-4 h-4 rounded-full bg-[#F4845F] text-white text-[10px] font-bold flex items-center justify-center leading-none">
          {activeFilterCount}
        </span>
      )}
      <ChevronUp className={`w-3 h-3 transition-transform duration-200 ${isOpen ? '' : 'rotate-180'}`} />
    </button>
  )
}

export default function FilterPanel({
  isOpen, prefersReducedMotion,
  statusFilter, setStatusFilter,
  inStockOnly, setInStockOnly,
  priceMin, setPriceMin,
  priceMax, setPriceMax,
  sortBy, setSortBy,
  clearFilters,
  activeFilterCount,
}: Props) {
  return (
    <AnimatePresence initial={false}>
      {isOpen && (
        <motion.div
          initial={{ height: prefersReducedMotion ? 'auto' : 0, opacity: 0 }}
          animate={{ height: 'auto', opacity: 1 }}
          exit={{ height: prefersReducedMotion ? 'auto' : 0, opacity: 0 }}
          transition={{ duration: prefersReducedMotion ? 0.1 : 0.22, ease: [0.16, 1, 0.3, 1] }}
          className="overflow-hidden"
        >
          <div className="flex flex-wrap gap-3 pb-4 pt-0 items-end">

            {/* Status */}
            <div className="flex flex-col gap-1">
              <span className="text-[10px] font-semibold text-[#6E6660] uppercase tracking-wide">סטטוס</span>
              <div className="flex gap-1.5 lg:gap-2">
                {([
                  ['all', 'הכל'],
                  ['active', 'פעיל'],
                  ['sale', 'מבצע'],
                  ['sold_out', 'אזל'],
                  ['other', 'אחר'],
                ] as const).map(([val, label]) => (
                  <button
                    key={val}
                    onClick={() => setStatusFilter(val)}
                    className={`px-3 py-1.5 rounded-full text-[12px] border-none cursor-pointer transition-all
                      ${statusFilter === val
                        ? 'bg-[#2D6A4F] text-white font-semibold'
                        : 'bg-[#F2EDE4] text-[#6B6560] hover:bg-[#EAE4DB]'}`}
                  >
                    {label}
                  </button>
                ))}
              </div>
            </div>

            {/* Price range */}
            <div className="flex flex-col gap-1">
              <span className="text-[10px] font-semibold text-[#6E6660] uppercase tracking-wide">מחיר (₪)</span>
              <div className="flex items-center gap-2">
                <input
                  type="number" min={0} placeholder="מ"
                  value={priceMin}
                  onChange={e => setPriceMin(e.target.value === '' ? '' : Number(e.target.value))}
                  className="w-16 border-[1.5px] border-[#F2EDE4] rounded-[8px] px-2 py-1.5 text-[12px]
                             bg-[#FAF7F2] text-center outline-none focus:border-[#2D6A4F]
                             focus:bg-white transition-all placeholder:text-[#6E6660]"
                />
                <span className="text-[12px] text-[#6E6660]">—</span>
                <input
                  type="number" min={0} placeholder="עד"
                  value={priceMax}
                  onChange={e => setPriceMax(e.target.value === '' ? '' : Number(e.target.value))}
                  className="w-16 border-[1.5px] border-[#F2EDE4] rounded-[8px] px-2 py-1.5 text-[12px]
                             bg-[#FAF7F2] text-center outline-none focus:border-[#2D6A4F]
                             focus:bg-white transition-all placeholder:text-[#6E6660]"
                />
              </div>
            </div>

            {/* In stock only */}
            <button
              onClick={() => setInStockOnly(!inStockOnly)}
              className={`flex items-center gap-2 px-3 py-2 rounded-full text-[12px] border-none
                          cursor-pointer transition-all self-end
                          ${inStockOnly
                            ? 'bg-[#2D6A4F] text-white font-semibold'
                            : 'bg-[#F2EDE4] text-[#6B6560] hover:bg-[#EAE4DB]'}`}
            >
              <span className={`w-3.5 h-3.5 rounded-sm border-2 flex items-center justify-center transition-colors
                ${inStockOnly ? 'bg-white border-white' : 'border-[#A89F97]'}`}
              >
                {inStockOnly && <span className="w-1.5 h-1.5 rounded-sm bg-[#2D6A4F]" />}
              </span>
              במלאי בלבד
            </button>

            {/* Sort */}
            <div className="flex flex-col gap-1">
              <span className="text-[10px] font-semibold text-[#6E6660] uppercase tracking-wide">מיון</span>
              <select
                value={sortBy}
                onChange={e => setSortBy(e.target.value as SortBy)}
                className="border-[1.5px] border-[#F2EDE4] rounded-[8px] px-3 py-1.5 text-[12px]
                           bg-[#FAF7F2] text-[#1A1A1A] outline-none focus:border-[#2D6A4F]
                           focus:bg-white transition-all appearance-none cursor-pointer"
              >
                <option value="default">ברירת מחדל</option>
                <option value="price_asc">מחיר: נמוך לגבוה</option>
                <option value="price_desc">מחיר: גבוה לנמוך</option>
                <option value="name">שם (א-ת)</option>
                <option value="qty_desc">כמות: הרבה למעט</option>
                <option value="qty_asc">כמות: מעט להרבה</option>
              </select>
            </div>

            {activeFilterCount > 0 && (
              <button
                onClick={clearFilters}
                className="flex items-center gap-1.5 px-3 py-2 rounded-full text-[12px] font-medium
                           text-[#C0392B] hover:bg-[#FFE8E0] border-none cursor-pointer
                           transition-colors self-end"
              >
                <XIcon className="w-3.5 h-3.5" />
                נקה הכל
              </button>
            )}
          </div>
        </motion.div>
      )}
    </AnimatePresence>
  )
}